import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';

const containerStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100%',
};

const headerStyle = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.sideDrawer.border}`,
});

const contentStyle = {
  overflow: 'auto' as const,
  flex: 1,
};

const footerStyle = (theme: Theme) => ({
  borderTop: `1px solid ${theme.palette.sideDrawer.border}`,
  padding: theme.spacing(2, 3),
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
});

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  anchor?: 'left' | 'right';
  size?: 'xs' | 'md' | 'lg';
  PaperProps?: any;
}

const SideDrawer: React.FC<SideDrawerProps> = React.memo(function SideDrawer({
  open,
  onClose,
  title,
  children,
  footer,
  anchor = 'right',
  size = 'md',
  PaperProps,
}) {
  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: (theme: Theme) => {
          const width = theme.drawerWidths[size] || theme.drawerWidths.md;
          return {
            width,
            maxWidth: '95vw',
            height: '100vh',
            boxShadow: theme.customShadows.drawer,
            borderLeft:
              anchor === 'right' ? `1px solid ${theme.palette.sideDrawer.border}` : undefined,
            borderRight:
              anchor === 'left' ? `1px solid ${theme.palette.sideDrawer.border}` : undefined,
            borderRadius: 0,
            ...(PaperProps?.sx || {}),
          };
        },
        ...PaperProps,
      }}
    >
      <Box sx={containerStyle}>
        {(title || onClose !== undefined) && (
          <Box sx={headerStyle}>
            <Typography
              sx={(theme: Theme) => ({
                fontWeight: 600,
                fontSize: 18,
                color: theme.palette.sideDrawer.title,
              })}
            >
              {title}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon
                sx={(theme: Theme) => ({ color: theme.palette.sideDrawer.icon, fontSize: 15 })}
              />
            </IconButton>
          </Box>
        )}
        <Box sx={contentStyle}>{children}</Box>
        {footer && <Box sx={footerStyle}>{footer}</Box>}
      </Box>
    </Drawer>
  );
});

export default SideDrawer;
